import {
  CommunicationWindowManager,
  FEATURES_DEFAULT_POPUP_WINDOW,
  getPopupFeatures,
  PopupWithBcHandler,
  randomId,
  WindowBlockAlertParams,
} from "@toruslabs/base-controllers";
import { encodeBase64Url } from "@toruslabs/metadata-helpers";
import type { AUTH_CONNECTION_TYPE, AuthConnectionConfig, ExtraLoginOptions, JRPCEngineV2, SafeEventEmitter } from "@web3auth/auth";
import { WEB3AUTH_NETWORK_TYPE, WhiteLabelData } from "@web3auth/auth";
import log from "loglevel";
import { Hex } from "viem";

import config from "../../config";
import { type AuthLoginPayload, type AuthPopupResponse, type AuthState, LOGIN_MODE, type LOGIN_MODE_TYPE } from "../../utils/interfaces";
import { generateLoginId, storeDataOnStorageServer } from "../../utils/utils";

class AuthWindowHandler {
  nonce = randomId();

  windowId?: string;

  isWindowAlreadyOpen?: boolean;

  authConnection: AUTH_CONNECTION_TYPE;

  authConnectionId?: string;

  groupedAuthConnectionId?: string;

  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;

  web3AuthClientId: string;

  /**
   * The client id of the dapp that is initiating the **EMBED** login.
   */
  embedDappClientId?: string;

  authConnectionConfig?: AuthConnectionConfig;

  finalURL: URL = new URL(config.baseRoute);

  extraLoginOptions: ExtraLoginOptions = {};

  sessionNamespace?: string;

  whiteLabel?: WhiteLabelData;

  loginMode: LOGIN_MODE_TYPE;

  constructor({
    windowId,
    authConnection,
    isWindowAlreadyOpen = false,
    authConnectionId,
    groupedAuthConnectionId,
    web3AuthNetwork,
    web3AuthClientId,
    extraLoginOptions,
    sessionNamespace,
    whiteLabel,
    authConnectionConfig,
    embedDappClientId,
    loginMode = LOGIN_MODE.main,
  }: {
    isWindowAlreadyOpen?: boolean;
    windowId?: string;
    authConnection: AUTH_CONNECTION_TYPE;
    authConnectionId?: string;
    groupedAuthConnectionId?: string;
    web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
    web3AuthClientId: string;
    extraLoginOptions: ExtraLoginOptions;
    sessionNamespace?: string;
    whiteLabel: WhiteLabelData;
    authConnectionConfig?: AuthConnectionConfig;
    /**
     * The login mode of the auth window handler.
     * @default LOGIN_MODE.main
     */
    loginMode?: LOGIN_MODE_TYPE;
    /**
     * The client id of the dapp that is initiating the **EMBED** login.
     */
    embedDappClientId?: string;
  }) {
    this.authConnection = authConnection;
    this.isWindowAlreadyOpen = isWindowAlreadyOpen;
    this.authConnectionId = authConnectionId;
    this.groupedAuthConnectionId = groupedAuthConnectionId;
    this.web3AuthNetwork = web3AuthNetwork;
    this.windowId = windowId;
    this.extraLoginOptions = extraLoginOptions;
    this.web3AuthClientId = web3AuthClientId;
    this.sessionNamespace = sessionNamespace;
    this.whiteLabel = whiteLabel;
    this.authConnectionConfig = authConnectionConfig;
    this.embedDappClientId = embedDappClientId;
    this.loginMode = loginMode;
  }

  async handleLoginWindow({
    communicationEngine,
    communicationWindowManager,
    communicationNotificationEmitter,
    handleWindowBlockAlert,
  }: {
    communicationEngine?: JRPCEngineV2;
    communicationNotificationEmitter?: SafeEventEmitter;
    communicationWindowManager?: CommunicationWindowManager;
    handleWindowBlockAlert?: (params: WindowBlockAlertParams) => Promise<null>;
  } = {}): Promise<AuthPopupResponse> {
    log.info("channel name", this.nonce);

    await this.setFinalUrl();

    const authWindow = new PopupWithBcHandler<AuthPopupResponse, never>({
      config: {
        communicationEngine,
        communicationEngineNotificationManager: communicationNotificationEmitter,
        communicationWindowManager,
        handleWindowBlockAlert,
        target: "_blank",
        features: getPopupFeatures(FEATURES_DEFAULT_POPUP_WINDOW),
        instanceId: this.isWindowAlreadyOpen ? this.windowId : this.nonce,
        timeout: this.isWindowAlreadyOpen ? 0 : 30000,
        isWindowAlreadyOpen: this.isWindowAlreadyOpen,
      },
      state: { url: this.finalURL, windowId: this.windowId },
      channelPrefix: config.loginChannelPrefix,
    });
    const result = await authWindow.handle();
    return result;
  }

  private async setFinalUrl(): Promise<void> {
    const finalUrl = new URL(`${config.baseRoute}start`);

    const loginId = generateLoginId();
    const state = this.getState(loginId);
    const sessionData: AuthLoginPayload = {
      authConnection: this.authConnection,
      authConnectionId: this.authConnectionId,
      groupedAuthConnectionId: this.groupedAuthConnectionId,
      authConnectionConfig: this.authConnectionConfig,
      state,
      windowId: this.windowId,
      sessionNamespace: this.sessionNamespace,
      extraLoginOptions: Object.keys(this.extraLoginOptions)?.length > 0 ? this.extraLoginOptions : undefined,
    };

    await storeDataOnStorageServer(loginId, sessionData, this.sessionNamespace);

    finalUrl.searchParams.append("state", state);
    if (this.sessionNamespace) finalUrl.searchParams.append("sessionNamespace", this.sessionNamespace);

    log.info(finalUrl.href);
    this.finalURL = finalUrl;
  }

  private getState(loginId: Hex): string {
    const state: AuthState = {
      instanceId: this.isWindowAlreadyOpen ? this.windowId : this.nonce,
      web3AuthNetwork: this.web3AuthNetwork,
      web3AuthClientId: this.web3AuthClientId,
      whiteLabel: this.whiteLabel,
      wsLoginId: loginId,
      embedDappClientId: this.embedDappClientId,
      // we need to pass the login mode to the state coz login window (`/start` route) can be opened in the new pop up, which has completely new context and
      // the login mode here, will not be available in the new window.
      loginMode: this.loginMode,
    };
    return encodeURIComponent(encodeBase64Url(JSON.stringify(state)));
  }
}

export default AuthWindowHandler;
